import React, { useEffect, useMemo, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import {
  Button,
  Card,
  Checkbox,
  Col,
  Dropdown,
  FormField,
  Icon,
  Row,
  Typography,
} from 'components/alf-design';
import MakePayment from 'components/dashboard/sections/Billing/MakePayment';
import { useForm } from 'utils';
import formats, { capitalize } from 'utils/formats';
import './Summary.css';

const options = [
  { label: 'Credit Card', value: 'CreditCard' },
  { label: 'Checking Account', value: 'ECheck' },
  { label: 'Savings Account', value: 'ECheck' },
];

const Summary = ({ data }) => {
  const [selected, setSelected] = useState(null);
  const [paymentType, setPaymentType] = useState(null);
  const [errors, setErrors] = useState(null);
  const [minAmount, setMinAmount] = useState(null);
  const [showPayment, togglePayment] = useState(false);
  const { handleChange, values, touched, setFormValues } = useForm({
    otherAmount: undefined,
  });
  const selectedPolicy = useMemo(
    () => ({
      ...data,
      isFusion: Boolean(data?.lineType),
      minAmountDue: minAmount,
      insured: {
        firstName: data?.insuredFirstName,
        lastName: data?.insuredLastName,
        zip: data?.insuredZip,
        street: data?.insuredStreet,
        city: data?.insuredCity,
        state: data?.insuredState,
      },
      paymentCategory: paymentType?.value,
    }),
    [data, minAmount, paymentType],
  );

  const handleCheckbox = (e) => {
    if (selected !== e.target.name) {
      handleChange(e);
      setSelected(e.target.name);
      setErrors(null);
    }
  };

  const handleInputChange = (e) => {
    e.target.value = e.target.value.replace(/[^0-9.]/g, '');
    setSelected('otherAmount');
    setErrors(null);
    handleChange(e);
  };

  const handleContinue = async () => {
    if (selected === null) {
      return setErrors({
        selected: 'Please select a payment amount.',
      });
    }

    if (selected === 'otherAmount') {
      if (values?.otherAmount === '' || values?.otherAmount === undefined) {
        return setErrors({ otherAmount: 'Other Amount Required' });
      } else {
        if (values?.otherAmount < data?.billing?.currentDueAmount) {
          return setErrors({
            otherAmount:
              'Payment amount cannot be less than the minimum amount due.',
          });
        }
        if (values?.otherAmount > data?.billing?.outstandingBalance) {
          return setErrors({
            otherAmount:
              'Payment amount cannot be greater than the remaining balance.',
          });
        }
        setErrors(null);
      }
    }

    if (paymentType === null) {
      return setErrors({
        paymentType: 'Please select a payment type.',
      });
    }

    if (errors === null) {
      togglePayment(true);
    }
  };

  useEffect(() => {
    if (selected === 'remainingBalance') {
      setFormValues({ otherAmount: '' }, false);
      setErrors(null);
      setMinAmount(data?.billing?.outstandingBalance);
    }
    if (selected === 'amountDue') {
      setFormValues({ otherAmount: '' }, false);
      setErrors(null);
      setMinAmount(data?.billing?.currentDueAmount);
    }
    if (selected === 'otherAmount') {
      setMinAmount(values?.otherAmount);
    }
  }, [selected, values?.otherAmount]);

  return (
    <Card className="summary-payment">
      {(data?.billing?.currentDueAmount === 0 ||
        data?.billing?.currentDueAmount === null) &&
        (data?.billing?.outstandingBalance === 0 ||
          data?.billing?.outstandingBalance === null) && (
          <div className="policyError">
            <Icon name="checkCircleFull" color="#34D399" />
            <Typography className="policyError-text">
              Your policy shows no remaining balance. No payment is needed at
              this time. If you have questions, contact Slide Customer Service.
            </Typography>
          </div>
        )}
      <div className="row policyInfo">
        <Col xs={23} md={11} className="inputLeft">
          <Typography className="subtitle">Policy Number:</Typography>
          <Typography className="value">{data?.policyNumber}</Typography>
        </Col>
        <Col xs={23} md={11} className="inputRight">
          <Typography className="subtitle">Policyholder Name:</Typography>
          <Typography className="value">{`${capitalize(data?.insuredFirstName)} ${capitalize(data?.insuredLastName)}`}</Typography>
        </Col>
      </div>
      <Row>
        <Typography className="subtitle">Insured Property:</Typography>
        <Typography className="value">{data?.insuredStreet}</Typography>
        <Typography className="value">{`${data?.insuredCity}, ${
          data?.insuredState
        } ${formats.zipFormat(data?.insuredZip)}`}</Typography>
      </Row>
      <hr className="hr"></hr>
      <Row className="customerService paymentAmountRow">
        <div className="paymentAmountOptions">
          <Checkbox
            id="remainingBalance"
            name="remainingBalance"
            className="checkbox"
            checked={selected === 'remainingBalance'}
            onChange={handleCheckbox}
            error={null}
            disabled={
              data?.billing?.outstandingBalance === 0 ||
              data?.billing?.outstandingBalance === null
            }
          >
            <Typography className="caption" type="caption1">
              Remaining Balance:{' '}
              <b className="checkboxMoney">
                {data?.billing
                  ? formats.moneyFormat(data?.billing?.outstandingBalance)
                  : '$0.00'}
              </b>
            </Typography>
          </Checkbox>
          <Checkbox
            id="amountDue"
            name="amountDue"
            className="checkbox"
            checked={selected === 'amountDue'}
            onChange={handleCheckbox}
            error={null}
            disabled={
              data?.billing?.currentDueAmount === 0 ||
              data?.billing?.currentDueAmount === null
            }
          >
            <Typography className="caption" type="caption1">
              Minimum Amount Due:{' '}
              <b className="checkboxMoney">
                {data?.billing
                  ? formats.moneyFormat(data?.billing?.currentDueAmount)
                  : '$0.00'}
              </b>
            </Typography>
          </Checkbox>
          <Checkbox
            id="otherAmount"
            name="otherAmount"
            className="checkbox otherAmount"
            checked={selected === 'otherAmount'}
            onChange={handleCheckbox}
            error={null}
          >
            <div className="otherAmountCaption">
              <Typography className="otherAmount" type="caption1">
                Other Amount:{' '}
              </Typography>
              <FormField
                label=""
                error={touched?.otherAmount ? errors?.otherAmount : ''}
                fullWidth={false}
              >
                <NumericFormat
                  id="otherAmountInput"
                  data-testid="otherAmountInput"
                  name="otherAmount"
                  onChange={handleInputChange}
                  thousandSeparator
                  prefix="$"
                  value={values?.otherAmount}
                />
              </FormField>
            </div>
          </Checkbox>
        </div>
        {errors?.selected && (
          <Typography type="error" component="span" className="inlineError">
            {errors?.selected}
          </Typography>
        )}
      </Row>
      <Row className="paymentType">
        <Typography className="subtitle">Payment Type:</Typography>
        <div className="paymentTypeControl">
          <Dropdown
            label="Select payment type"
            options={options}
            className="breakdown"
            onSelect={setPaymentType}
            error={null}
            setError={setErrors}
          />
          {errors?.paymentType && (
            <Typography type="error" component="span" className="inlineError">
              {errors?.paymentType}
            </Typography>
          )}
        </div>
      </Row>
      <Row>
        <span className="span">
          The Other amount field may be used to enter an alternate payment
          amount. An amount less than the Minimum Amount Due or greater than the
          Remaining balance will not be accepted. If this policy is on an
          installment plan, entering an alternate payment amount will result in
          an adjustment to future installment amounts.
        </span>
      </Row>
      <Row>
        <span className="span">
          This transaction is payment for an insurance policy. There are no
          returns or refunds. Please refer to the policy for terms and
          conditions concerning cancellation and refund of unearned premium. If
          this transaction is for the purchase of a new insurance policy, the
          policy declarations will be mailed within three business days.
        </span>
      </Row>
      <Row className="customerService">
        If there are questions regarding this payment or if technical
        difficulties occur, please contact Slide Insurance Company Customer
        Service{' '}
        <a
          data-testid="customer-service"
          data-dd-action-name="payment.customer_service.call"
          href={`tel:800-748-2030`}
          className="phone"
          tabIndex="0"
        >
          (800) 748-2030.
        </a>
      </Row>
      <Row className="buttonRow">
        <Button
          id="continue-login"
          data-dd-action-name="payment.summary.continue"
          type="primary"
          onClick={handleContinue}
          className="continue-button"
          disabled={
            (data?.billing?.currentDueAmount === 0 ||
              data?.billing?.currentDueAmount === null) &&
            (data?.billing?.outstandingBalance === 0 ||
              data?.billing?.outstandingBalance === null)
          }
        >
          Continue
        </Button>
      </Row>

      <div id="portalOneContainer"></div>
      <MakePayment
        show={showPayment}
        onClose={() => togglePayment(false)}
        selectedPolicy={selectedPolicy}
        oneTimePayment={true}
      />
    </Card>
  );
};

export default Summary;
